// Alert Config Slice — Redux state for Sequence Detect configuration profiles
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { alertConfigService, type AlertConfigProfile } from '../../services/alertConfigService';
import type { ThresholdConfig, DEFAULT_THRESHOLDS } from '../../components/sequenceDetect/behaviorTypes';

// Re-export DEFAULT_THRESHOLDS for use in components
export { DEFAULT_THRESHOLDS };

export interface AlertConfigState {
  profiles: AlertConfigProfile[];
  activeProfileId: string | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: AlertConfigState = {
  profiles: [],
  activeProfileId: null,
  isLoading: false,
  error: null,
};

// ─── Async Thunks ─────────────────────────────────────────────────────────────

export const fetchProfiles = createAsyncThunk(
  'alertConfig/fetchProfiles',
  async (_, { rejectWithValue }) => {
    try {
      const profiles = await alertConfigService.listConfigs();
      return profiles;
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to fetch profiles');
    }
  }
);

export const saveProfile = createAsyncThunk(
  'alertConfig/saveProfile',
  async (
    input: {
      id?: string;
      name: string;
      thresholds: ThresholdConfig;
      filters?: Record<string, boolean>;
      activityDrop?: number;
      mergeGapSec?: number;
      isDefault?: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      const profile = await alertConfigService.saveConfig(input);
      if (!profile) {
        return rejectWithValue('Failed to save profile');
      }
      return profile;
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to save profile');
    }
  }
);

export const deleteProfile = createAsyncThunk(
  'alertConfig/deleteProfile',
  async (id: string, { rejectWithValue }) => {
    try {
      const success = await alertConfigService.deleteConfig(id);
      if (!success) {
        return rejectWithValue('Cannot delete default profile or profile not found');
      }
      return id;
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to delete profile');
    }
  }
);

export const setDefaultProfile = createAsyncThunk(
  'alertConfig/setDefaultProfile',
  async (id: string, { rejectWithValue }) => {
    try {
      const profile = await alertConfigService.setDefaultConfig(id);
      if (!profile) {
        return rejectWithValue('Failed to set default profile');
      }
      return profile;
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to set default profile');
    }
  }
);

// ─── Slice ─────────────────────────────────────────────────────────────────────

const alertConfigSlice = createSlice({
  name: 'alertConfig',
  initialState,
  reducers: {
    setActiveProfile(state, action: PayloadAction<string | null>) {
      state.activeProfileId = action.payload;
    },
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // fetchProfiles
    builder
      .addCase(fetchProfiles.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchProfiles.fulfilled, (state, action) => {
        state.isLoading = false;
        state.profiles = action.payload;
        // Auto-select the default profile if none selected
        if (!state.activeProfileId) {
          const defaultProfile = action.payload.find((p) => p.isDefault);
          if (defaultProfile) {
            state.activeProfileId = defaultProfile.id;
          } else if (action.payload.length > 0) {
            state.activeProfileId = action.payload[0].id;
          }
        }
      })
      .addCase(fetchProfiles.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // saveProfile
    builder
      .addCase(saveProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(saveProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        const saved = action.payload;
        const idx = state.profiles.findIndex((p) => p.id === saved.id);
        if (idx >= 0) {
          state.profiles[idx] = saved;
        } else {
          state.profiles.push(saved);
        }
        // If this is the default, update other profiles
        if (saved.isDefault) {
          state.profiles.forEach((p) => {
            if (p.id !== saved.id) {
              p.isDefault = false;
            }
          });
        }
        state.activeProfileId = saved.id;
      })
      .addCase(saveProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // deleteProfile
    builder
      .addCase(deleteProfile.fulfilled, (state, action) => {
        const deletedId = action.payload;
        state.profiles = state.profiles.filter((p) => p.id !== deletedId);
        if (state.activeProfileId === deletedId) {
          // Switch to the default profile or the first one
          const defaultProfile = state.profiles.find((p) => p.isDefault);
          state.activeProfileId = defaultProfile?.id || state.profiles[0]?.id || null;
        }
      });

    // setDefaultProfile
    builder
      .addCase(setDefaultProfile.fulfilled, (state, action) => {
        const updated = action.payload;
        state.profiles.forEach((p) => {
          p.isDefault = p.id === updated.id;
        });
      });
  },
});

export const { setActiveProfile, clearError } = alertConfigSlice.actions;
export default alertConfigSlice.reducer;